import { useState } from "react";
import { openExternal } from "../lib/open-external";
import { useNavigate } from "react-router-dom";
import { Heart, ExternalLink } from "lucide-react";
import { T, scoreColor, withAlpha } from "../tokens";
import { ScoreRing }  from "../components/ScoreRing";
import { ResumeClarityCallout } from "../components/ResumeClarityCallout";
import { MatchReadinessPanel } from "../components/MatchReadinessPanel";
import { Badge }      from "../components/Badge";
import { Btn }        from "../components/Btn";
import { useReveal } from "../hooks/useMotion";
import { useMatches, useMatchStats, useSaveMutation, useGenerateMatches } from "../hooks/useMatches";
import { useActiveResume } from "../hooks/useResume";

// ── helpers ──────────────────────────────────────────────────────────────

function weekLabel() {
  const now   = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const week  = Math.ceil(((now.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7);
  const day   = now.toLocaleDateString("en-US", { weekday: "long" });
  return `${day} · Week ${week}`;
}

function timeAgo(dateStr: string | null) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "< 1h ago";
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Divider() {
  return <div style={{ height: "1px", background: T.b0, margin: "24px 0" }}/>;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3, letterSpacing: "0.12em",
      textTransform: "uppercase", marginBottom: "14px" }}>
      {children}
    </div>
  );
}

// Open the job's external portal (Caliber never applies on your behalf).
function openPortal(url?: string | null) {
  openExternal(url);
}

// ── Hero "Pick of the week" — top match. Whole card opens the portal. ─────
function HeroCard({ match, onSave }: { match: any; onSave: () => void }) {
  const [h, setH] = useState(false);
  const score = Math.round(match.weightedScore ?? 0);
  const rev   = useReveal(200, 14);
  const clickable = !!match.url;

  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      onClick={() => openPortal(match.url)}
      style={{
        ...rev,
        background: T.s1, border: `1px solid ${T.tealBdr}`,
        borderRadius: "12px", padding: "24px",
        position: "relative", overflow: "hidden",
        boxShadow: h ? `0 4px 32px ${T.tealGlow}` : "none",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        borderColor: h ? withAlpha(T.teal, 0.38) : T.tealBdr,
        cursor: clickable ? "pointer" : "default",
      }}
    >
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "1px",
        background: `linear-gradient(90deg, transparent, ${withAlpha(T.teal, 0.56)}, transparent)` }}/>
      {h && (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none",
          background: `linear-gradient(105deg, transparent 40%, ${withAlpha(T.teal, 0.03)} 50%, transparent 60%)` }}/>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "12px" }}>
            <Badge variant="teal" size="sm" pulse>pick of the week</Badge>
            {match.postedAt && <Badge variant="default" size="sm">{timeAgo(match.postedAt)}</Badge>}
            {match.workAuthTag?.includes("remote") && <Badge variant="info" size="sm">remote</Badge>}
          </div>

          <h2 style={{ fontFamily: T.display, fontSize: "22px", fontWeight: 700, color: T.t0,
            margin: "0 0 4px", lineHeight: 1.2,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {match.title}
          </h2>
          <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: "0 0 16px" }}>
            {match.company}
            {match.location && ` · ${match.location}`}
            {match.salaryRaw && ` · ${match.salaryRaw}`}
          </p>

          {match.explanation && (
            <div style={{ padding: "12px 14px", background: T.s2, borderRadius: "8px",
              borderLeft: `2px solid ${T.teal}`, marginBottom: "16px" }}>
              <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t1, margin: 0, lineHeight: 1.6 }}>
                <span style={{ color: T.tealSoft, fontWeight: 500 }}>Insight · </span>
                {match.explanation}
              </p>
            </div>
          )}

          {/* Actions — Caliber opens the portal; you apply there yourself. */}
          <div style={{ display: "flex", gap: "8px" }} onClick={(e) => e.stopPropagation()}>
            {match.url && (
              <Btn variant="teal" size="md" icon={<ExternalLink size={13} strokeWidth={1.8}/>}
                onClick={() => openPortal(match.url)}>Open posting</Btn>
            )}
            <Btn variant="ghost" size="md" icon={<Heart size={13} strokeWidth={1.8}/>} onClick={onSave}>Save</Btn>
          </div>
        </div>

        <ScoreRing score={score} size={80} delay={300}/>
      </div>
    </div>
  );
}

// ── Compact strong-fit row — whole row opens the portal. ─────────────────
function StrongFitRow({ match, index, onSave }: { match: any; index: number; onSave: () => void }) {
  const [h, setH] = useState(false);
  const score = Math.round(match.weightedScore ?? 0);
  const rev   = useReveal(400 + index * 80, 10);
  const clickable = !!match.url;

  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      onClick={() => openPortal(match.url)}
      style={{
        ...rev,
        display: "flex", alignItems: "center", gap: "14px",
        background: h ? T.s2 : T.s1, border: `1px solid ${h ? T.b2 : T.b0}`,
        borderRadius: "10px", padding: "14px 16px",
        transition: "all 0.15s ease", cursor: clickable ? "pointer" : "default",
      }}
    >
      <ScoreRing score={score} size={48} showLabel={false} delay={500 + index * 80}/>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: T.display, fontSize: "15px", fontWeight: 600, color: T.t0,
          marginBottom: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {match.title}
        </div>
        <div style={{ fontFamily: T.body, fontSize: "12px", color: T.t2,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {match.company}
          {match.location && ` · ${match.location}`}
          {match.salaryRaw && ` · ${match.salaryRaw}`}
        </div>
      </div>

      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
        {match.workAuthTag?.includes("remote") && <Badge variant="info" size="sm">remote</Badge>}
        {match.postedAt && <Badge variant="default" size="sm">{timeAgo(match.postedAt)}</Badge>}
      </div>

      <div style={{ display: "flex", gap: "6px", flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        <Btn variant="ghost" size="sm" onClick={onSave}><Heart size={14} strokeWidth={1.8}/></Btn>
        {match.url && <Btn variant="outline" size="sm" onClick={() => openPortal(match.url)}>Open →</Btn>}
      </div>
    </div>
  );
}

// ── Stat tile (rail). Count tiles navigate; score/analytics tiles are display. ─
function StatTile({ label, value, accent, to }: {
  label: string; value: string | number; accent?: string; to?: string;
}) {
  const navigate = useNavigate();
  const [h, setH] = useState(false);
  const clickable = !!to;
  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      onClick={clickable ? () => navigate(to!) : undefined}
      role={clickable ? "button" : undefined}
      style={{
        background: h && clickable ? T.s3 : T.s2, border: `1px solid ${T.b1}`,
        borderRadius: "8px", padding: "12px 14px",
        cursor: clickable ? "pointer" : "default", transition: "background 0.12s ease",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.10em",
        textTransform: "uppercase", marginBottom: "6px" }}>
        <span>{label}</span>
        {clickable && <span style={{ color: h ? T.tealSoft : T.t3 }}>→</span>}
      </div>
      <div style={{ fontFamily: T.mono, fontSize: "20px", fontWeight: 700,
        color: accent ?? T.t0, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
    </div>
  );
}

interface Stats { totalMatches: number; avgScore: number; autoApply: number; applied: number; topScore: number; }

function StatRail({ stats, resumeState }: { stats?: Stats | undefined; resumeState: "loading" | "active" | "none" }) {
  const rev = useReveal(300, 10);
  const resumeValue = resumeState === "loading" ? "…" : resumeState === "active" ? "Active" : "—";
  return (
    <div style={{ ...rev, display: "flex", flexDirection: "column", gap: "10px" }}>
      <SectionLabel>This week</SectionLabel>
      <StatTile label="Total matches" value={stats?.totalMatches ?? "—"}
        accent={stats?.totalMatches ? T.t0 : T.t3} to="/matches" />
      <StatTile label="Strong fit roles" value={stats?.autoApply ?? "—"}
        accent={stats?.autoApply ? T.ok : T.t3} to="/matches?minScore=85" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
        <StatTile label="Top score" value={stats?.topScore || "—"}
          accent={stats?.topScore ? scoreColor(stats.topScore) : T.t3} />
        <StatTile label="Avg score" value={stats?.avgScore != null ? Math.round(stats.avgScore) : "—"}
          accent={stats?.avgScore != null ? scoreColor(stats.avgScore) : T.t3} />
      </div>
      <StatTile label="Applied" value={stats?.applied ?? "—"} accent={stats?.applied ? T.ok : T.t3} />
      <StatTile label="Resume" value={resumeValue}
        accent={resumeState === "active" ? T.ok : T.t3} to="/profile" />
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export function Dashboard() {
  const { data: stats }                 = useMatchStats();
  const { data: topMatches, isLoading } = useMatches({ minScore: 75, limit: 6 });
  const save    = useSaveMutation();
  const refresh = useGenerateMatches();

  // Shared key/hook (same one ResumeGate and Matches.tsx use) — previously this
  // page ran its own separate query under a different cache key, causing a
  // second /api/resumes/active request on every Dashboard load for no reason.
  const activeResumeQuery = useActiveResume();

  const hasResume = activeResumeQuery.isSuccess;
  const noResume  = activeResumeQuery.isError
    && (activeResumeQuery.error as { response?: { status?: number } })?.response?.status === 404;
  const resumeState: "loading" | "active" | "none" =
    activeResumeQuery.isLoading ? "loading" : hasResume ? "active" : "none";

  const hero       = topMatches?.[0];
  const strongFits = topMatches?.slice(1) ?? [];

  const headerRev = useReveal(0, 10);

  return (
    <div style={{ maxWidth: "1280px", margin: "0 auto", width: "100%" }}>

      {/* ── Masthead ─────────────────────────────────────────────────── */}
      <div style={{ ...headerRev, marginBottom: "24px" }}>
        <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
          letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "6px" }}>
          {weekLabel()}
        </div>
        <h1 style={{ fontFamily: T.display, fontSize: "26px", fontWeight: 700,
          color: T.t0, margin: 0, lineHeight: 1.15 }}>
          Career Intelligence
        </h1>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, marginTop: "4px" }}>
          Your personalised weekly briefing
        </p>
      </div>

      <ResumeClarityCallout
        clarity={activeResumeQuery.data?.clarity}
        to="/profile#target-role"
      />

      {/* ── Two-column: main feed + stat rail (wraps to stack on narrow) ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", alignItems: "flex-start", marginTop: "20px" }}>

        {/* MAIN — pick of the week + strong fits */}
        <div style={{ flex: "999 1 520px", minWidth: 0 }}>
          {isLoading && (
            <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "12px",
              padding: "40px", textAlign: "center" }}>
              <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t3 }}>Loading matches…</p>
            </div>
          )}

          {/* ResumeGate already guarantees an active resume on this route in the
              normal case; `noResume` only fires if the gate failed open on a
              transient error (see ResumeGate.tsx). Everything else routes
              through MatchReadinessPanel, which reads the ACTUAL reason
              (ambiguous role / matching disabled / thin inventory / never
              generated / genuinely none) instead of one static message. */}
          {!isLoading && !hero && noResume && (
            <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "12px",
              padding: "40px", textAlign: "center" }}>
              <p style={{ fontFamily: T.display, fontSize: "18px", color: T.t1, margin: "0 0 8px" }}>
                No matches yet
              </p>
              <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0 }}>
                Upload your resume to find matching jobs.
              </p>
            </div>
          )}
          {!isLoading && !hero && !noResume && <MatchReadinessPanel />}

          {hero && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <SectionLabel>Pick of the week</SectionLabel>
                {/* Shown once matches exist — the manual, ALWAYS-recompute
                    action. The first match set is generated automatically
                    (no button) — see MatchReadinessPanel.tsx. */}
                <Btn
                  variant="ghost" size="sm"
                  disabled={refresh.isPending}
                  onClick={() => refresh.mutate()}
                >
                  {refresh.isPending ? "Refreshing…" : "Refresh Matches"}
                </Btn>
              </div>
              <HeroCard match={hero} onSave={() => save.mutate(hero)} />
            </>
          )}

          {strongFits.length > 0 && (
            <>
              <Divider/>
              <SectionLabel>Strong fits · {strongFits.length} this week</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {strongFits.map((m, i) => (
                  <StrongFitRow key={m.matchId} match={m} index={i} onSave={() => save.mutate(m)} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* RAIL — consolidated stats (single source; count tiles navigate) */}
        <aside style={{ flex: "1 1 240px", minWidth: 0 }}>
          <StatRail stats={stats} resumeState={resumeState} />
        </aside>

      </div>
    </div>
  );
}
